import React from "react";
import {
    InputGroup,
    Menu,
    MenuItem,
    Spinner,
    Tag,
    Icon,
    Button
} from "@blueprintjs/core";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../../micro/fns/util";
import { getStatusMeta as getStatusMetaGlobal } from "../../constants/statusColors";
import _ from "lodash";
import moment from "moment";
import { withRouter } from "react-router-dom";

class SurgerySearch extends React.Component {
  _searchSeq = 0;
  searchAreaRef = React.createRef();

  state = {
    searchQuery: "",
    isSearching: false,
    results: [],
    showResults: false,
    devErrorText: "",
  };

    getStatusMeta = (status) => getStatusMetaGlobal(status);

  componentDidMount() {
    document.addEventListener("mousedown", this.handleDocumentMouseDown);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleDocumentMouseDown);
  }

    handleDocumentMouseDown = () => {
        // Keep results sticky; do not auto-close on outside clicks
        return;
    };

  runSearch = async (query) => {
    const trimmedQuery = String(query || "").trim();
    if (!trimmedQuery || trimmedQuery.length < 2) {
      this.setState({
        results: [],
        showResults: false,
        isSearching: false,
        devErrorText: "",
      });
      return;
    }

    const searchSeq = ++this._searchSeq;

    const prefix = this.props.authStore?.prefix || "default";
    const url = `/v3/${prefix}/neoSxBooking/quickSearch`;
    const body = { query: trimmedQuery, limit: 10, includeOld: true };

    try {
      this.setState({ isSearching: true, showResults: true, devErrorText: "" });

      if (process.env.NODE_ENV !== "production") {
        console.log("[V3 SurgerySearch] quickSearch request", { url, body });
      }

      const response = await apiCallPost(url, body);

      // Ignore stale responses if user typed again
      if (searchSeq !== this._searchSeq) return;

      const items = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.results)
            ? response.results
            : [];

      this.setState({
        results: items || [],
        isSearching: false,
      });
    } catch (err) {
      // Ignore stale errors if user typed again
      if (searchSeq !== this._searchSeq) return;

      const status = err?.response?.status;
      const data = err?.response?.data;

      console.error("[V3 SurgerySearch] quickSearch error", {
        url,
        body,
        status,
        data,
      });

      let devErrorText = "";
      if (process.env.NODE_ENV !== "production") {
        if (data) {
          devErrorText =
            typeof data === "string" ? data : JSON.stringify(data, null, 2);
        } else if (status) {
          devErrorText = `HTTP ${status}`;
        } else if (err?.message) {
          devErrorText = err.message;
        } else {
          devErrorText = "Unknown error";
        }

        if (status) {
          devErrorText = `HTTP ${status}: ${devErrorText}`;
        }
      }

      this.setState({ isSearching: false, devErrorText });
      // We don't want to spam errors during typing
    }
  };

  searchSurgeries = _.debounce(async (query) => {
    await this.runSearch(query);
  }, 300);

  handleInputChange = (e) => {
    const query = e.target.value;
    this.setState({ searchQuery: query });
    if (query.length >= 2) {
      this.searchSurgeries(query);
    } else {
      this.setState({ results: [], showResults: false, devErrorText: "" });
    }
  };

    handleResultClick = (sx) => {
        // Navigate to surgery details page in a new tab
        const targetId = sx?._id;
        if (!targetId) {
            return;
        }
        const detailsPath = `/v3/workflow/surgery-booking/${targetId}`;
        window.open(detailsPath, "_blank", "noopener,noreferrer");
    };

  handleManualSearch = () => {
    // Cancel any pending debounced search and run immediately
    this.searchSurgeries?.cancel?.();
    this.runSearch(this.state.searchQuery);
  };

  render() {
    const { searchQuery, isSearching, results, showResults, devErrorText } =
      this.state;

    const resultsMenu = (
        <Menu
            style={{
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                minWidth: "900px",
                padding: "8px",
            }}
        >
        {isSearching ? (
          <MenuItem disabled icon={<Spinner size={16} />} text="Searching..." />
        ) : results.length === 0 ? (
          <MenuItem disabled text="No surgeries found matching your search" />
        ) : (
            results.map((sx) => {
                const statusMeta = this.getStatusMeta(sx.status);
                const lateralityText = sx?.laterality
                    ? _.startCase(String(sx.laterality).toLowerCase())
                    : "N/A";
                return (
                    <MenuItem
                        key={sx._id}
                        onClick={() => this.handleResultClick(sx)}
                        multiline={true}
                        text={
                            <div style={{ padding: "8px 0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                                    <span style={{ fontWeight: "bold", color: "#106ba3", fontSize: "1.1em" }}>
                                        {sx.bookingReference || sx.entityName || "No ID"}
                                    </span>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <Tag
                                            minimal
                                            large
                                            style={{
                                                color: statusMeta.color,
                                                backgroundColor: statusMeta.background,
                                                border: `1px solid ${statusMeta.border}`,
                                            }}
                                        >
                                            Status: {statusMeta.label}
                                        </Tag>
                                        <Tag minimal intent="primary" large>
                                            Sx Date: {sx.sxDate ? moment(sx.sxDate).format("DD MMM YYYY") : "No Date"}
                                        </Tag>
                                    </div>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                                    <span style={{ fontWeight: "600", fontSize: "1em" }}>{sx.patientName || "Unknown Patient"}</span>
                                    <span style={{ color: "var(--text-secondary)", fontWeight: "500" }}>{sx.patientPid || "No PID"}</span>
                                </div>
                                <div style={{ fontSize: "0.9em", color: "var(--text-secondary)", marginBottom: "4px" }}>
                                    <Icon icon="cut" size={14} style={{ marginRight: "8px" }} />
                                    <span style={{ fontWeight: "500" }}>Surgery:</span>{" "}
                                    {sx.surgeryName ||
                                        (sx.selectedSurgeries && sx.selectedSurgeries.map((s) => s.name).join(", ")) ||
                                        "N/A"}
                                </div>
                                <div style={{ fontSize: "0.9em", color: "var(--text-secondary)", marginBottom: "4px" }}>
                                    <Icon icon="eye-open" size={14} style={{ marginRight: "8px" }} />
                                    <span style={{ fontWeight: "500" }}>Laterality:</span> {lateralityText}
                                </div>
                                <div style={{ fontSize: "0.9em", color: "var(--text-secondary)", marginBottom: "4px" }}>
                                    <Icon icon="person" size={14} style={{ marginRight: "8px" }} />
                                    <span style={{ fontWeight: "500" }}>Doctor:</span> {sx.surgeonName || "N/A"}
                                </div>
                                <div style={{ fontSize: "0.9em", color: "var(--text-secondary)" }}>
                                    <Icon icon="credit-card" size={14} style={{ marginRight: "8px" }} />
                                    <span style={{ fontWeight: "500" }}>Payor Panel:</span> {sx.payorPanel || "N/A"}
                                </div>
                            </div>
                        }
                    />
                );
            })
        )}
      </Menu>
    );

    const isDev = process.env.NODE_ENV !== "production";

    return (
      <div className="bp3-dark" style={{ 
        padding: "20px", 
        backgroundColor: "var(--bg-primary)", 
        minHeight: "100vh"
      }}>
        <div style={{ width: "100%", maxWidth: "2400px" }}>
          <div style={{ marginBottom: "12px" }}>
            <div style={{ color: "var(--text-primary)", fontWeight: 600, fontSize: "32px", lineHeight: 1.15 }}>
              Search surgery
            </div>
          </div>

          <div style={{ color: "var(--text-secondary)", fontSize: "18px", lineHeight: 1.25, marginBottom: "10px" }}>
            {(() => {
              const isRecc = (this.props.authStore?.prefix || "").toLowerCase() === "recc";
              return isRecc
                ? "Search using PID (patientId), SxID (docket number), patient name, or phone number. Examples: DBG26E0800001, REC0000000003, 9382990037."
                : "Search using PID (patientId), SxID (docket number), patient name, or phone number. Examples: BKP26A0701415, P0101944634, 9836826396.";
            })()}
          </div>

          <div ref={this.searchAreaRef} style={{ width: "100%" }}>
            <InputGroup
              large
              fill
              autoFocus
              leftIcon="search"
              placeholder="Type PID / SxID / name / phone"
              value={searchQuery}
              onChange={this.handleInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  this.handleManualSearch();
                }
              }}
              rightElement={
                <div style={{ display: "flex", alignItems: "center", height: "100%", paddingRight: "8px" }}>
                  {isSearching && <Spinner size={18} />}
                  <Button
                    minimal
                    icon="search"
                    intent="primary"
                    disabled={isSearching || !searchQuery || String(searchQuery).trim().length < 2}
                    onClick={this.handleManualSearch}
                    title="Search"
                  />
                  {searchQuery && !isSearching && (
                    <Button
                      minimal
                      icon="cross"
                      onClick={() => this.setState({ searchQuery: "", results: [], showResults: false })}
                    />
                  )}
                </div>
              }
              style={{ width: "100%" }}
            />

            {isDev && devErrorText ? (
              <pre
                style={{
                  marginTop: "10px",
                  padding: "10px",
                  borderRadius: "6px",
                  backgroundColor: "rgba(219, 55, 55, 0.08)",
                  border: "1px solid rgba(219, 55, 55, 0.35)",
                  color: "var(--text-primary)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontSize: "12px",
                  lineHeight: 1.35,
                }}
              >
                {devErrorText}
              </pre>
            ) : null}

            {showResults && String(searchQuery || "").trim().length >= 2 ? (
              <div style={{ marginTop: "8px", width: "100%" }}>{resultsMenu}</div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(inject("authStore")(observer(SurgerySearch)));
